import { useEffect, useRef, useState, useCallback } from 'react';
import { useCasesStore } from '../store/casesStore';

const POLL_INTERVAL = 30; // 30 seconds

/**
 * Global hook that polls Salesforce queue and runs detection logic.
 * This ensures returned/lost/found cases are detected regardless of which page the user is on.
 * Should be called once at the app level.
 */
export function useSalesforcePolling(): void {
  const { fetchSalesforceCases } = useCasesStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Initial fetch
    fetchSalesforceCases();

    // Set up polling
    intervalRef.current = setInterval(() => {
      fetchSalesforceCases();
    }, POLL_INTERVAL * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchSalesforceCases]);
}

// Store for sharing countdown state across components
let countdownListeners: Array<(count: number) => void> = [];
let currentCountdown = POLL_INTERVAL;
let countdownInterval: ReturnType<typeof setInterval> | null = null;

function startCountdownTimer() {
  if (countdownInterval) return;

  countdownInterval = setInterval(() => {
    currentCountdown = currentCountdown <= 1 ? POLL_INTERVAL : currentCountdown - 1;
    countdownListeners.forEach(listener => listener(currentCountdown));
  }, 1000);
}

function resetCountdown() {
  currentCountdown = POLL_INTERVAL;
  countdownListeners.forEach(listener => listener(currentCountdown));
}

/**
 * Hook to get the current SF sync countdown.
 * Can be used in any component to display the countdown.
 */
export function useSalesforceCountdown(): { countdown: number; reset: () => void } {
  const [countdown, setCountdown] = useState(currentCountdown);

  useEffect(() => {
    countdownListeners.push(setCountdown);
    startCountdownTimer();

    return () => {
      countdownListeners = countdownListeners.filter(l => l !== setCountdown);
      if (countdownListeners.length === 0 && countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
    };
  }, []);

  const reset = useCallback(() => {
    resetCountdown();
  }, []);

  return { countdown, reset };
}
